import Image from 'next/image';
import {
  FormEvent,
  HTMLInputTypeAttribute,
  ReactNode,
  useCallback,
} from 'react';

import warning from './assets/warning.svg';

export type TextInputProps = {
  label: ReactNode;
  value: string;
  setValue?: (value: string | { (value: string): string }) => unknown;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  button?: {
    text: string;
    onSubmit: (value: string) => unknown;
  };
  maxLength?: number;
  type?: HTMLInputTypeAttribute;
  onSubmit?: (value: string) => unknown;
};

const TextInput = (props: TextInputProps) => {
  const {
    label,
    value,
    placeholder,
    error,
    disabled,
    button,
    maxLength,
    type = `text`,
    onSubmit,
  } = props;

  const handleSubmit = useCallback(
    async (event: MouseEvent | FormEvent) => {
      event.preventDefault();
      if (onSubmit) {
        onSubmit(value);
      }
    },
    [onSubmit, value],
  );
  const handleClick = useCallback(
    async (event: MouseEvent | FormEvent) => {
      event.preventDefault();
      button?.onSubmit(value);
    },
    [button, value],
  );

  const setValue = (value: string) => {
    if (!props.setValue) {
      return;
    }

    return maxLength
      ? props.setValue(value.slice(0, maxLength))
      : props.setValue(value);
  };

  if (disabled) {
    return (
      <label className={`flex w-full flex-col`}>
        <span className={`font-roboto text-b2 font-normal text-gray-100`}>
          {label}
        </span>

        <form className={`relative mt-[4px] mb-[8px]`} onSubmit={handleSubmit}>
          <input
            type={type}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className={`w-full appearance-none rounded-[8px] border border-gray-25 bg-[transparent] py-[13px] pl-[16px] font-roboto text-b2 font-normal text-gray-600 opacity-100 placeholder:text-gray-50`}
            style={{
              paddingRight: button ? `95px` : `16px`,
              WebkitTextFillColor: `#fafafa`,
            }}
            placeholder={placeholder}
            disabled={true}
          />
          {button && (
            <button
              onClick={handleClick}
              type={`button`}
              className={`absolute right-[6px] top-[50%] translate-y-[-50%] rounded-[8px] bg-gray-10 px-[16px] py-[9px] font-roboto text-h5 font-normal text-gray-200`}
            >
              {button.text}
            </button>
          )}
        </form>
        <div className={`flex items-center`}>
          <span className={`invisible text-c2 font-normal text-danger-red-200`}>
            {`Invisible`}
          </span>
        </div>
      </label>
    );
  }
  if (error) {
    return (
      <label className={`flex w-full flex-col`}>
        <span className={`font-roboto text-b2 font-normal text-gray-100`}>
          {label}
        </span>

        <form className={`relative mt-[4px] mb-[8px]`} onSubmit={handleSubmit}>
          <input
            type={type}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className={`w-full appearance-none rounded-[8px] bg-gray-25 py-[13px] pl-[16px] font-roboto text-b2 font-normal text-gray-600 outline-none ring-1 ring-danger-red-100 placeholder:text-gray-50 ${
              button ? `pr-[95px]` : `pr-[16px] `
            }`}
            placeholder={placeholder}
          />
          {button && (
            <button
              onClick={handleClick}
              type={`button`}
              className={`absolute right-[6px] top-[50%] translate-y-[-50%] rounded-[8px] bg-gray-10 px-[16px] py-[9px] font-roboto text-h5 font-normal text-gray-200`}
            >
              {button.text}
            </button>
          )}
        </form>
        <div className={`flex items-center gap-[4px]`}>
          <Image width={16} height={16} src={warning} alt="warning" />
          <span className={`text-c2 font-normal text-danger-red-200`}>
            {error}
          </span>
        </div>
      </label>
    );
  }

  return (
    <label className={`flex w-full flex-col`}>
      <span className={`font-roboto text-b2 font-normal text-gray-100`}>
        {label}
      </span>

      <form className={`relative mt-[4px] mb-[8px]`} onSubmit={handleSubmit}>
        <input
          type={type}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={`w-full appearance-none rounded-[8px] bg-gray-25 py-[13px] pl-[16px] font-roboto text-b2 font-normal text-gray-600 placeholder:text-gray-50 focus:outline-none focus:ring-1 focus:ring-green-100 ${
            button ? `pr-[95px]` : `pr-[16px] `
          }`}
          placeholder={placeholder}
        />
        {button && (
          <button
            onClick={handleClick}
            type={`button`}
            className={`absolute right-[6px] top-[50%] translate-y-[-50%] rounded-[8px] bg-gray-10 px-[16px] py-[9px] font-roboto text-h5 font-normal text-gray-200`}
          >
            {button.text}
          </button>
        )}
      </form>
      <div className={`flex items-center`}>
        <span className={`invisible text-c2 font-normal text-danger-red-200`}>
          {`Invisible`}
        </span>
      </div>
    </label>
  );
};

export default TextInput;
